import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import Alert from "../../../Common/Alert";
import DatatableFunc from "../../../Common/DatatableFunc";
import Http from "../../../Common/Http";
import Swal from "sweetalert2";
import IsLogin from "../../../Common/IsLogin";
import { LoaderComponent } from "../../../Common/LoaderComponent";
import { ProgressSpinner } from "primereact/progressspinner";

const LeaveReport = () => {
  const location = useLocation();
  const [receiveData, setReceiveData] = useState([]);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);

  // Pagination and search states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");

  // Color palette for departments
  const departmentColors = [
    "bg-blue-50 border-l-4 border-blue-400",
    "bg-green-50 border-l-4 border-green-400",
    "bg-purple-50 border-l-4 border-purple-400",
    "bg-orange-50 border-l-4 border-orange-400",
    "bg-pink-50 border-l-4 border-pink-400",
    "bg-indigo-50 border-l-4 border-indigo-400",
    "bg-yellow-50 border-l-4 border-yellow-400",
    "bg-red-50 border-l-4 border-red-400",
    "bg-teal-50 border-l-4 border-teal-400",
    "bg-cyan-50 border-l-4 border-cyan-400",
  ];

  const fetchData = () => {
    setLoading(true);
    Http.get("/report-leave-report").then((res) => {
      setReceiveData(res.data);
      setLoading(false);
    });
  };

  useEffect(() => {
    DatatableFunc();
    fetchData();
  }, []);

  const showAlert = (message, type) => {
    setAlert({
      message: message,
      type: type,
    });
    setTimeout(() => {
      setAlert(null);
    }, 4000);
  };

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  // Filter and search logic
  const filteredData = useMemo(() => {
    let filtered = receiveData;

    // Department filter
    if (departmentFilter) {
      filtered = filtered.filter((dept) =>
        dept.dept_name.toLowerCase().includes(departmentFilter.toLowerCase())
      );
    }

    // Search filter (searches in department name and staff names)
    if (searchTerm) {
      filtered = filtered.filter((dept) => {
        const deptMatch = dept.dept_name
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
        const staffMatch = dept.staff.some(
          (staff) =>
            staff.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            staff.card_no.toLowerCase().includes(searchTerm.toLowerCase())
        );
        return deptMatch || staffMatch;
      });
    }

    return filtered;
  }, [receiveData, searchTerm, departmentFilter]);

  // Calculate total staff count
  const totalStaff = useMemo(() => {
    return filteredData.reduce((total, dept) => total + dept.staff.length, 0);
  }, [filteredData]);

  // Pagination logic for flattened data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    let flattenedData = [];
    let currentIndex = 0;

    for (let deptIndex = 0; deptIndex < filteredData.length; deptIndex++) {
      const dept = filteredData[deptIndex];

      // Add department header
      if (currentIndex >= startIndex && currentIndex < endIndex) {
        flattenedData.push({
          type: "department",
          data: dept,
          index: currentIndex,
          colorClass: departmentColors[deptIndex % departmentColors.length],
        });
      }
      currentIndex++;

      // Add staff members
      for (let staffIndex = 0; staffIndex < dept.staff.length; staffIndex++) {
        if (currentIndex >= startIndex && currentIndex < endIndex) {
          flattenedData.push({
            type: "staff",
            data: dept.staff[staffIndex],
            deptData: dept,
            index: currentIndex,
            colorClass: departmentColors[deptIndex % departmentColors.length],
          });
        }
        currentIndex++;
      }
    }

    return {
      data: flattenedData,
      totalItems: currentIndex,
      totalPages: Math.ceil(currentIndex / itemsPerPage),
    };
  }, [filteredData, currentPage, itemsPerPage, departmentColors]);

  // Get unique departments for filter
  const departments = useMemo(() => {
    return [...new Set(receiveData.map((dept) => dept.dept_name))];
  }, [receiveData]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(parseInt(items));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setDepartmentFilter("");
    setCurrentPage(1);
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div>
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <h5 className="am-title">Leave Report</h5>
          </div>
          <div className="am-pagebody">
            <Alert alert={alert} />
          </div>
          <div className="am-pagebody">
            <div className="card pd-20 pd-sm-40">
              {/* Search and Filter Controls */}
              <div className="row mb-3">
                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor="search" className="form-label">
                      Search
                    </label>
                    <input
                      type="text"
                      id="search"
                      className="form-control"
                      placeholder="Search by name, card no, or department..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label htmlFor="department" className="form-label">
                      Department
                    </label>
                    <select
                      id="department"
                      className="form-control"
                      value={departmentFilter}
                      onChange={(e) => {
                        setDepartmentFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">All Departments</option>
                      {departments.map((dept) => (
                        <option key={dept} value={dept}>
                          {dept}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="form-group">
                    <label htmlFor="itemsPerPage" className="form-label">
                      Items per page
                    </label>
                    <select
                      id="itemsPerPage"
                      className="form-control"
                      value={itemsPerPage}
                      onChange={(e) => handleItemsPerPageChange(e.target.value)}
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label className="form-label">&nbsp;</label>
                    <div>
                      <button
                        className="btn btn-secondary btn-sm me-2"
                        onClick={resetFilters}
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary Info */}
              <div className="row mb-3">
                <div className="col-12">
                  <div className="alert alert-info">
                    <strong>Summary:</strong> Showing{" "}
                    {
                      paginatedData.data.filter((item) => item.type === "staff")
                        .length
                    }{" "}
                    staff from {totalStaff} total staff across{" "}
                    {filteredData.length} departments
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="table-responsive pb-2">
                  {loading ? (
                    <ProgressSpinner />
                  ) : (
                    <>
                      <table className="table table-sm table-bordered table-striped align-items-center justify-content-center mb-0">
                        <thead className="table-dark">
                          <tr>
                            <th className="text-uppercase text-sm font-weight-bolder ps-2">
                              S/N
                            </th>
                            <th className="text-uppercase text-sm font-weight-bolder ps-2">
                              Card No
                            </th>
                            <th className="text-uppercase text-sm font-weight-bolder ps-2">
                              Name
                            </th>
                            <th
                              className="text-uppercase text-sm font-weight-bolder ps-2 text-center"
                              colSpan={2}
                            >
                              Leave Allowanced
                            </th>
                            <th
                              className="text-uppercase text-sm font-weight-bolder ps-2 text-center"
                              colSpan={2}
                            >
                              Leave Taken
                            </th>
                            <th
                              className="text-uppercase text-sm font-weight-bolder ps-2 text-center"
                              colSpan={2}
                            >
                              Balance
                            </th>
                          </tr>
                          <tr>
                            <th className="text-uppercase text-sm font-weight-bolder ps-2"></th>
                            <th className="text-uppercase text-sm font-weight-bolder ps-2"></th>
                            <th className="text-uppercase text-sm font-weight-bolder ps-2"></th>
                            <th className="text-uppercase text-sm font-weight-bolder ps-2">
                              Casual
                            </th>
                            <th className="text-uppercase text-sm font-weight-bolder ps-2">
                              Sick
                            </th>
                            <th className="text-uppercase text-sm font-weight-bolder ps-2">
                              Casual
                            </th>
                            <th className="text-uppercase text-sm font-weight-bolder ps-2">
                              Sick
                            </th>
                            <th className="text-uppercase text-sm font-weight-bolder ps-2">
                              Casual
                            </th>
                            <th className="text-uppercase text-sm font-weight-bolder ps-2">
                              Sick
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedData.data.map((item, index) => {
                            if (item.type === "department") {
                              return (
                                <tr
                                  key={`dept-${item.data.id}`}
                                  className={item.colorClass}
                                >
                                  <td className="text-sm font-weight-bold">
                                    {Math.floor(item.index / itemsPerPage) *
                                      itemsPerPage +
                                      index +
                                      1}
                                  </td>
                                  <td className="text-sm"></td>
                                  <td
                                    className="text-sm font-weight-bold"
                                    colSpan={7}
                                  >
                                    🏢 {item.data.dept_name} (
                                    {item.data.staff.length} staff)
                                  </td>
                                </tr>
                              );
                            } else {
                              const casualBalance =
                                (item.deptData.declared_leave_casual > 0
                                  ? item.deptData.declared_leave_casual
                                  : 0) -
                                (item.data.casual ? item.data.casual : 0);
                              const sickBalance =
                                (item.deptData.declared_leave_sick > 0
                                  ? item.deptData.declared_leave_sick
                                  : 0) -
                                (item.data.sick > 0 ? item.data.sick : 0);

                              return (
                                <tr
                                  key={`staff-${item.data.id}`}
                                  className={`${item.colorClass.replace(
                                    "border-l-4",
                                    "border-l-2"
                                  )} hover:shadow-sm transition-shadow`}
                                >
                                  <td className="text-xs"></td>
                                  <td className="text-xs font-medium">
                                    {item.data.card_no}
                                  </td>
                                  <td className="text-xs">{item.data.name}</td>
                                  <td className="text-xs text-center">
                                    <span className="badge bg-primary">
                                      {item.deptData.declared_leave_casual}
                                    </span>
                                  </td>
                                  <td className="text-xs text-center">
                                    <span className="badge bg-primary">
                                      {item.deptData.declared_leave_sick}
                                    </span>
                                  </td>
                                  <td className="text-xs text-center">
                                    <span className="badge bg-warning">
                                      {item.data.casual || 0}
                                    </span>
                                  </td>
                                  <td className="text-xs text-center">
                                    <span className="badge bg-warning">
                                      {item.data.sick || 0}
                                    </span>
                                  </td>
                                  <td className="text-xs text-center">
                                    <span
                                      className={`badge ${
                                        casualBalance < 0
                                          ? "bg-danger"
                                          : casualBalance === 0
                                          ? "bg-secondary"
                                          : "bg-success"
                                      }`}
                                    >
                                      {casualBalance}
                                    </span>
                                  </td>
                                  <td className="text-xs text-center">
                                    <span
                                      className={`badge ${
                                        sickBalance < 0
                                          ? "bg-danger"
                                          : sickBalance === 0
                                          ? "bg-secondary"
                                          : "bg-success"
                                      }`}
                                    >
                                      {sickBalance}
                                    </span>
                                  </td>
                                </tr>
                              );
                            }
                          })}
                        </tbody>
                      </table>

                      {/* Pagination Controls */}
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div>
                          <span className="text-sm text-muted">
                            Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                            {Math.min(
                              currentPage * itemsPerPage,
                              paginatedData.totalItems
                            )}{" "}
                            of {paginatedData.totalItems} entries
                          </span>
                        </div>
                        <nav>
                          <ul className="pagination pagination-sm">
                            <li
                              className={`page-item ${
                                currentPage === 1 ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                              >
                                Prev
                              </button>
                            </li>

                            {/* Page numbers */}
                            {Array.from(
                              { length: Math.min(5, paginatedData.totalPages) },
                              (_, i) => {
                                let pageNum;
                                if (paginatedData.totalPages <= 5) {
                                  pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                  pageNum = i + 1;
                                } else if (
                                  currentPage >=
                                  paginatedData.totalPages - 2
                                ) {
                                  pageNum = paginatedData.totalPages - 4 + i;
                                } else {
                                  pageNum = currentPage - 2 + i;
                                }

                                return (
                                  <li
                                    key={pageNum}
                                    className={`page-item ${
                                      currentPage === pageNum ? "active" : ""
                                    }`}
                                  >
                                    <button
                                      className="page-link"
                                      onClick={() => handlePageChange(pageNum)}
                                    >
                                      {pageNum}
                                    </button>
                                  </li>
                                );
                              }
                            )}

                            <li
                              className={`page-item ${
                                currentPage === paginatedData.totalPages
                                  ? "disabled"
                                  : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={
                                  currentPage === paginatedData.totalPages
                                }
                              >
                                Next
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaveReport;
